import { useContext, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { BoxFull, Button, Dropdown } from "iuvity-ui-components";
import { CustomInput } from "../inputs/CustomInput";
import { NewDialogProps } from "../dialog/NewCustomDialog";
import { useTransactions } from "../../hooks/useTransactions";
import { useVersions } from "../../hooks/useVersions";
import { convertValueTypes } from "../../utils";
import { TRX_BENEFICIARY_OPTIONS, TRX_TYPE_OPTIONS } from "../../constants";
import { TrxGeneralInfoFormSchema } from "../../validations/schemas";
import { ErrorMessage } from "@hookform/error-message";
import { AppContext } from "../../context/index";

interface TrxGeneralInfoProps {
  filter: "ID" | "NO-ID";
  fieldActionFn: Function;
  auxModalController: React.Dispatch<React.SetStateAction<NewDialogProps>>;
}

const transactionKeys = [
  "name",
  "systemfunction",
  "labelkey",
  "wildcardcode",
  "authorizationflowname",
  "type",
  "beneficiaryregistrationtype",
  "fieldorder",
  "isbatch",
  "status",
  "releaseversion",
];

export const TrxGeneralInfo: React.FC<TrxGeneralInfoProps> = ({
  filter,
  fieldActionFn,
  auxModalController,
}) => {
  const navigate = useNavigate();
  const {
    t,
    appState: { user, selectedTransaction },
  } = useContext(AppContext);

  const { data, transactionAction } = useTransactions("ID");
  const { data: versionsData } = useVersions("DATA");

  const {
    control,
    getValues,
    reset,
    formState: { errors, dirtyFields, isDirty, isValid },
  } = useForm({
    mode: "onChange",
    delayError: 1200,
    reValidateMode: "onChange",
  });

  const onSave = async () => {
    const formValues = getValues();
    const dirtyFieldsKeys: any[] = Object.keys(dirtyFields);
    const fieldsKeys = dirtyFieldsKeys.filter(
      (x) => transactionKeys.indexOf(x) === -1
    );

    if (transactionKeys.some((r) => dirtyFieldsKeys.indexOf(r) >= 0)) {
      const newValues = Object.assign(
        {},
        ...getValues(transactionKeys).map((value, index) => ({
          [transactionKeys[index]]:
            transactionKeys[index] === "status"
              ? value
                ? "ACTIVE"
                : "INACTIVE"
              : value,
        }))
      );
      await transactionAction("UPDATE", [
        {
          id: selectedTransaction?.id,
          updatedby: user?.name,
          ...newValues,
        },
      ]);
    }

    if (fieldsKeys.length > 0) {
      const propsData = (data && data[0]?.transactionproperties) || [];
      const newFieldsValues = getValues(fieldsKeys).map((value, index) => ({
        id: propsData.find((prop: any) => prop.key === fieldsKeys[index])!.id,
        value: `${value}`,
      }));
      await fieldActionFn("PROPERTY", "UPDATE", newFieldsValues);
    }

    reset(formValues);
  };

  const onCancel = () => {
    navigate("/transactions");
  };

  const handleModalProps = (isCancelButton?: boolean) => {
    auxModalController((prev) => ({
      ...prev,
      visible: true,
      title: isCancelButton
        ? t("Transactions.cancelModalTitle")
        : t("Transactions.saveModalTitle"),
      content: isCancelButton
        ? t("Transactions.cancelModalContent")
        : t("Transactions.saveModalContent"),
      onConfirm: isCancelButton ? onCancel : onSave,
    }));
  };

  const formOptions = useMemo(() => {
    if (versionsData) {
      return {
        type: TRX_TYPE_OPTIONS(t),
        beneficiaryregistrationtype: TRX_BENEFICIARY_OPTIONS(t),
        releaseversion: versionsData
          .map((version: any) => ({
            label: version.name,
            value: version.name,
          }))
          .sort((a: any, b: any) => b.label.localeCompare(a.label)),
      };
    } else {
      return null;
    }
  }, [t, versionsData]);

  useEffect(() => {
    if (data) {
      const initialValues = data[0].transactionproperties
        ?.filter((prop) => (filter === "ID" ? prop.id : !prop.id))
        ?.map((property: any) => {
          const valKey = property.key.split(".").pop() || "";
          return {
            [valKey]: convertValueTypes(property.value),
          };
        })
        .reduce((acc: any, current: any) => ({
          ...acc,
          ...current,
        }));

      reset(initialValues);
    }
  }, [data, reset, filter]);

  return (
    data &&
    formOptions && (
      <BoxFull className="px-0">
        <div className="flex align-items-flex-start justify-content-flex-start flex-wrap">
          {Object.keys(getValues()).length > 0 && (
            <>
              {data[0].transactionproperties
                ?.filter((prop) => filter === "ID" ? prop.id : !prop.id)
                ?.map((prop) => ({
                  ...prop,
                  label: t(`Transactions.${prop?.label || `${prop?.key}Label`}`),
                }))
                ?.sort(function (a, b) {
                  return (
                    b?.type.localeCompare(a?.type) ||
                    a?.label.localeCompare(b?.label)
                  );
                })
                .map((property: any) => {
                  return (
                    <div
                      key={`c-${property.key}`}
                      className="flex flex-column mb-1"
                      style={{ width: "50%" }}
                    >
                      <Controller
                        key={property.key}
                        name={property.key}
                        control={control}
                        rules={
                          (TrxGeneralInfoFormSchema(t) as any)[property.key]
                        }
                        render={({ field }) => {
                          let extraProps = {
                            keyfilter:
                              property?.type === "number"
                                ? /^[0-9]/
                                : (TrxGeneralInfoFormSchema(t) as any)[
                                    property.key
                                  ]?.keyfilter,
                            maxLength: (TrxGeneralInfoFormSchema(t) as any)[
                              property.key
                            ]?.maxLength?.value,
                          };
                          return [
                            "type",
                            "beneficiaryregistrationtype",
                            "releaseversion",
                          ].includes(property.key) ? (
                            // @ts-ignore
                            <Dropdown
                              className="dropdown mb-3"
                              label={property.label}
                              options={(formOptions as any)[property.key]}
                              filter={false}
                              showClear={false}
                              width="20rem"
                              {...field}
                            />
                          ) : (
                            <CustomInput
                              id={`input-${property.key}`}
                              key={property.key}
                              label={property.label}
                              className={`${
                                errors[property.key]?.message
                                  ? "p-invalid"
                                  : "input-md mb-4"
                              }`}
                              placeholder={`${t(
                                "Products.propPlaceholder"
                              )} ${property.label.toLowerCase()}`}
                              type={
                                property.key === "cardBackgroundColor"
                                  ? "color"
                                  : property?.type === "number"
                                  ? "text"
                                  : property?.type
                              }
                              autoComplete="off"
                              style={{
                                width: "20rem",
                                height: `${
                                  property.key === "cardBackgroundColor" &&
                                  "2.2rem"
                                }`,
                                paddingBottom: `${
                                  property.key === "cardBackgroundColor" &&
                                  "0rem"
                                }`,
                              }}
                              {...extraProps}
                              {...field}
                            />
                          );
                        }}
                      />
                      {property.type !== "boolean" && (
                        <ErrorMessage
                          errors={errors}
                          name={property.key}
                          //@ts-ignore
                          render={({ message }) => (
                            <small
                              className="p-error block"
                              style={{
                                marginTop: "0.5rem",
                                marginBottom: "1.5rem",
                              }}
                            >
                              {message}
                            </small>
                          )}
                        />
                      )}
                    </div>
                  );
                })}
            </>
          )}
        </div>
        <div className="ap-selector-btn-c mt-2">
          <Button
            className="ap-selector-btn-c__cancel"
            variant="p-button-text"
            label={t("Transactions.cancelBtnLabel")}
            onClick={!isDirty ? onCancel : () => handleModalProps(true)}
          />
          <Button
            variant="p-button"
            label={t("Products.saveBtnLabel")}
            disabled={!(isDirty && isValid)}
            onClick={() => handleModalProps()}
          />
        </div>
      </BoxFull>
    )
  );
};
